import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { RiCloseLine, RiTerminalLine } from "react-icons/ri";
import { v4 as uuidv4 } from "uuid";

// Asset Imports
// Previous bubble gif: import TWOflyWave from "../components/assets/2flyWave.gif";
import TWOflyWave from "../images/IMG_2169.png";

// --- Retro Animations ---
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

// --- Styled Components ---

const ChatbotBubble = styled.div`
  position: fixed;
  bottom: 25px;
  right: 25px;
  width: 60px;
  height: 60px;
  border-radius: 4px; /* Square/Boxy retro look */
  background: #000;
  border: 1px solid #43fe47;
  cursor: pointer;
  z-index: 9999;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(67, 254, 71, 0.4);
  transition: all 0.2s ease;
  &:hover { box-shadow: 0 0 20px #43fe47; transform: translateY(-2px); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 10px;
`;

const TerminalWindow = styled.div`
  width: 100%;
  max-width: 520px;
  height: 600px;
  background: #000;
  border: 2px solid #43fe47;
  display: flex;
  flex-direction: column;
  font-family: "VT323", monospace !important;
  position: relative;
  overflow: hidden;

  /* Scanline Effect Overlay */
  &::before {
    content: " ";
    position: absolute;
    top: 0; left: 0; bottom: 0; right: 0;
    background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.1) 50%);
    background-size: 100% 4px;
    z-index: 10;
    pointer-events: none;
    opacity: 0.3;
  }
`;

const Header = styled.div`
  background: #43fe47;
  color: #000;
  padding: 6px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 1.1rem;
`;

const ChatBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  scrollbar-width: thin;
  scrollbar-color: #43fe47 #000;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #000; }
  &::-webkit-scrollbar-thumb { background: #43fe47; }
`;

const MessageWrapper = styled.div`
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const TextContent = styled.div`
  max-width: 90%;
  color: #43fe47;
  font-family: "VT323", monospace !important; /* Force same font everywhere */
  font-size: 1.2rem; /* Slightly larger for the terminal feel */
  line-height: 1.2;
  background: ${props => props.isUser ? 'rgba(67, 254, 71, 0.1)' : 'transparent'};
  padding: ${props => props.isUser ? '4px 10px' : '0'};
  border: ${props => props.isUser ? '1px solid #43fe47' : 'none'};
  word-wrap: break-word;

  /* Improved Strong Tag: Bright white with a digital glow */
  strong {
    color: #fff;
    font-weight: normal;
    font-family: "VT323", monospace !important;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
    letter-spacing: 0.5px;
    font-size: 1.5rem;
  }

  /* Improved Link: Underlined and reactive */
  a {
    color: #00ffff; /* Cyan is the 'friendly' 80s high-tech color */
    font-family: "VT323", monospace !important;
    text-decoration: none;
    border-bottom: 1px dashed #00ffff; /* Signals it's a link without being 'ugly' */
    transition: all 0.2s ease;

    &:hover {
      color: #fff;
      background: rgba(0, 255, 255, 0.2);
      border-bottom: 1px solid #fff;
      text-shadow: 0 0 10px #00ffff;
    }
  }

  /* Keep Strong as White for that sharp 'Header' look */
  strong {
    color: #fff;
    font-weight: normal;
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.4);
  }
`;

const LoadingText = styled.div`
  color: #43fe47;
  font-size: 1rem;
  margin-top: 5px;
  &:after {
    content: "...";
    animation: ${blink} 1s infinite;
  }
`;

const OptionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const OptionButton = styled.button`
  background: transparent;
  color: #43fe47;
  border: 1px solid #43fe47;
  padding: 4px 10px;
  font-family: "VT323", monospace !important;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.1s;

  &:hover {
    background: #43fe47;
    color: #000;
  }
  &:disabled { opacity: 0.3; cursor: default; }
`;

const CalendlyButton = styled.button`
  background: #fff;
  color: #000;
  border: none;
  padding: 1px 6px;
  margin: 4px 0;
  font-family: "VT323", monospace !important;
  font-size: 1rem;
  cursor: pointer;
`;

const TerminalFooter = styled.div`
  border-top: 1px solid #43fe47;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #43fe47;
  font-size: 1.1rem;
`;

const Cursor = styled.div`
  width: 8px;
  height: 16px;
  background: #43fe47;
  animation: ${blink} 1s infinite;
`;

// --- Main Component ---

const MyChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatRef = useRef(null);

 const conversationNodes = {
  start: {
    response: "Hey there — I’m James, the Idea Alchemist behind StudioVoice2Fly. I build AI tools that blend psychology, accessibility, and design. What would you like to explore?",
    options: ["about_studio", "products", "showcase", "socials", "contact", "exit"],
  },
  about_studio: {
    response: "StudioVoice2Fly builds AI powered platforms at psychology + tech intersection. Mission: Purpose-driven tools for neurodivergents, education, and immersive experiences.",
    options: ["purpose", "skills", "products", "showcase", "contact", "exit"],
  },
  purpose: {
    response: "Our mission is to humanize AI — creating tools that empower learning, creativity, and neurodivergent expression. We believe technology should amplify individuality, not replace it.",
    options: ["skills", "products", "showcase", "socials", "contact", "exit"],
  },
  products: {
    response: (
      <>
        • <strong>HomeschoolGuide.AI</strong><br />
        &nbsp;&nbsp;Flagship platform. AI lesson plans & tools for neurodivergent homeschooling.<br /><br />
        • <strong>form-rag-js (NPM)</strong><br />
        &nbsp;&nbsp;A developer-first solution for structured AI business intake via Grok.<br /><br />
        • <strong>Archetype API</strong><br />
        &nbsp;&nbsp;JSON delivery system for Jungian profile integration.<br /><br />
        • <strong>Small Biz Bots</strong><br />
        &nbsp;&nbsp;Conversion-focused automated assistants.<br /><br />
        • <strong>AI-PWA Suite</strong><br />
        &nbsp;&nbsp;Sector-specific apps for Legal, Real Estate, and Skilled Trades.<br /><br />
        • <strong>Archédex & MindPulse-150</strong><br />
        &nbsp;&nbsp;Psych-tech tools exploring personality and archetypal storytelling.<br /><br />
      </>
    ),
    options: ["showcase", "skills", "socials", "contact", "start", "exit"],
  },
  skills: {
    response: (
      <>
        <strong>Core Capabilities:</strong><br />
        • Creative AI Systems & Chatbots<br />
        • Symbolic Storytelling & Brand Design<br />
        • Archetype Character Development<br />
        • Adaptive Learning Tools<br />
        • Neurodivergent-Friendly UX
      </>
    ),
    options: ["products", "showcase", "about_studio", "contact", "start", "exit"],
  },
  contact: {
    response: (
      <>
        Contact path active: <a href="mailto:james@studiovoice2fly.com">Email Me</a> or{" "}
        <CalendlyButton onClick={() => window.open("https://cal.com/james-studiovoice2fly.com/30min", "_blank")}>
          Schedule a Call
        </CalendlyButton>
      </>
    ),
    options: ["showcase", "products", "socials", "start", "exit"],
  },
  socials: {
    response: (
      <>
        • <strong>LinkedIn</strong><br />
        &nbsp;&nbsp;Professional Intel: <a href="https://www.linkedin.com/in/james-rrsantos" target="_blank">Connect</a><br /><br />
        • <strong>GitHub</strong><br />
        &nbsp;&nbsp;Source Code & Repos: <a href="https://github.com/jrq3rq" target="_blank">View Files</a><br /><br />
        • <strong>X / Twitter</strong><br />
        &nbsp;&nbsp;Latest Updates: <a href="https://x.com/studiovoice2fly" target="_blank">Follow</a><br /><br />
      </>
    ),
    options: ["showcase", "products", "contact", "start", "exit"],
  },
  showcase: {
    response: (
      <>
        • <strong>HomeschoolGuide.AI</strong><br />
        &nbsp;&nbsp;Flagship Startup: <a href="https://homeschoolguide.ai/signin" target="_blank">View Live</a><br /><br />
        • <strong>form-rag-js</strong><br />
        &nbsp;&nbsp;NPM Package: <a href="https://www.npmjs.com/package/form-rag-js" target="_blank">Download Terminal</a><br /><br />
        • <strong>Voice of the Magi</strong><br />
        &nbsp;&nbsp;Grok Integration: <a href="https://voices-of-the-magi.web.app/" target="_blank">Access Link</a><br /><br />
        • <strong>Archetype Academy</strong><br />
        &nbsp;&nbsp;Nexus Hub: <a href="https://archetype-academy-nexus.web.app/" target="_blank">Access Link</a><br /><br />
      </>
    ),
    options: ["products", "skills", "socials", "contact", "start", "exit"],
  },
  exit: {
    response: "Thanks for stopping by! If inspiration strikes later, email me at james@studiovoice2fly.com or book a chat anytime.",
    options: ["start"], // Added start back in case they clicked exit by mistake
  },
};

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ id: uuidv4(), role: "assistant", content: conversationNodes.start.response, options: conversationNodes.start.options }]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  const handleOption = (nodeId) => {
    const node = conversationNodes[nodeId];
    if (!node) return;

    setMessages(prev => [...prev, { id: uuidv4(), role: "user", content: nodeId.replace(/_/g, " ").toUpperCase() }]);
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      setMessages(prev => [...prev, { id: uuidv4(), role: "assistant", content: node.response, options: node.options }]);
      if (nodeId === "exit") setTimeout(() => setIsOpen(false), 4000);
    }, 800);
  };

  return (
    <>
      <ChatbotBubble onClick={() => setIsOpen(true)}>
        <img src={TWOflyWave} alt="Chat" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </ChatbotBubble>

      {isOpen && (
        <ModalOverlay onClick={() => setIsOpen(false)}>
          <TerminalWindow onClick={e => e.stopPropagation()}>
            <Header>
             <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                letterSpacing: '1px',
                fontSize: '0.9rem'
                }}>
                {/* The icon in Cyan makes it look like an active power light */}
                <RiTerminalLine style={{ color: '#000' }} />
                <span style={{ color: '#000' }}>SV2F_ENGINE
                </span>
            </div>
              <RiCloseLine style={{ cursor: 'pointer', fontSize: '1.5rem' }} onClick={() => setIsOpen(false)} />
            </Header>

            <ChatBody ref={chatRef}>
              {messages.map((m) => (
                <MessageWrapper key={m.id} isUser={m.role === "user"}>
                  <TextContent isUser={m.role === "user"}>
                    {m.role === 'user' ? `> ${m.content}` : m.content}
                  </TextContent>
                  {!loading && m.options && (
                    <OptionContainer>
                      {m.options.map(opt => (
                        <OptionButton key={opt} onClick={() => handleOption(opt)}>
                          {opt.replace(/_/g, " ")}
                        </OptionButton>
                      ))}
                    </OptionContainer>
                  )}
                </MessageWrapper>
              ))}
              {loading && <LoadingText>ACCESSING_DATABASE</LoadingText>}
            </ChatBody>

            <TerminalFooter>
              <span>{">"}</span>
              <span style={{ opacity: 0.6 }}>{loading ? "TRANSFERRING..." : "READY"}</span>
              <Cursor />
            </TerminalFooter>
          </TerminalWindow>
        </ModalOverlay>
      )}
    </>
  );
};

export default MyChatbot;